import React, { useState } from 'react';
import { Modal, Button } from 'reactstrap';
import { useMutation } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaste } from '@fortawesome/free-solid-svg-icons';
import classnames from 'classnames';
import { searchTickers } from 'services/analysis';
import { parseTabularData } from 'helpers/string';

function SpreadsheetData({ data, searched, isMulti }) {
  if (!data || data.length === 0 || !searched) return;

  return (
    <div className="tw-overflow-x-auto">
      <table className="tw-min-w-full tw-border tw-border-gray-300 tw-shadow-md tw-rounded-md">
        <thead>
          <tr className="tw-bg-gray-200 tw-text-gray-700 tw-text-center">
            <th className="tw-border tw-px-4 tw-py-2">Symbol</th>
            <th className="tw-border tw-px-4 tw-py-2">Weight1</th>
            {isMulti && (
              <>
                <th className="tw-border tw-px-4 tw-py-2">Weight2</th>
                <th className="tw-border tw-px-4 tw-py-2">Weight3</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => {
            const ticker = row[0];
            const notFound = searched.list_not_found.includes(ticker);

            return (
              <tr
                key={rowIndex}
                className={classnames('tw-hover:bg-gray-100', {
                  'tw-text-red-500': notFound,
                })}
              >
                {row.map((cell, colIndex) => (
                  <td key={colIndex} className="tw-border tw-px-4 tw-py-2 tw-text-center">
                    {cell}
                    {notFound && colIndex === 0 && <span className="tw-text-red-500">*</span>}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {searched.list_not_found.length > 0 && (
        <div class="tw-flex tw-items-center tw-bg-yellow-200 tw-text-yellow-800 tw-px-4 tw-py-2 tw-rounded-md tw-shadow-sm tw-mb-4">
          <span className="tw-text-red-500">We couldn’t find these symbols with *.</span>
        </div>
      )}
    </div>
  );
}

function ExampleDataTable() {
  const rows = [
    { symbol: 'RY:TSX', value: 10 },
    { symbol: 'AGLR:NEO', value: 10 },
    { symbol: 'XIC:TSX', value: 10 },
    { symbol: 'AAPL', value: 7 },
    { symbol: 'JNK', value: 5 },
    { symbol: 'FID2024', value: 10 },
  ];

  return (
    <div className="tw-overflow-x-auto">
      <table className="tw-min-w-full tw-table-auto tw-border-collapse tw-border tw-border-solid tw-border-black">
        <tbody>
          {rows.map(({ symbol, value }) => (
            <tr key={symbol}>
              <td className="tw-border tw-border-solid tw-border-black tw-px-2 tw-py-1 tw-font-bold">{symbol}</td>
              <td className="tw-border tw-border-solid tw-border-black tw-px-2 tw-py-1 tw-text-center">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CustomDataModal({ isOpen, isMulti = true, onClose }) {
  const [data, setData] = useState(null);
  const [searched, setSearched] = useState(null);

  const { mutateAsync: _searchTickers, isPending: isSearchingTickers } = useMutation({
    mutationFn: searchTickers,
  });

  const handlePaste = async (event) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');

    const numberOfColumns = isMulti ? 4 : 2;
    const parsedData = parseTabularData(pastedData, numberOfColumns);
    const tickets = parsedData.map((row) => _.trim(row[0]));
    const searched = await _searchTickers(tickets);

    setSearched(searched);
    setData(parsedData);
  };

  const handleSubmit = async () => {
    const validData = data.filter((row) => !searched.list_not_found.includes(row[0].trim()));
    const validMetaData = validData.map((v) => {
      const meta = searched.clean_output[v[0].trim()];
      return {
        database: meta[0],
        asset: meta[1],
        weight1: v[1],
        weight2: v[2] ?? 0,
        weight3: v[3] ?? 0,
      };
    });

    setData(null);
    setSearched(null);
    await onClose(validMetaData);
  };

  return (
    <Modal isOpen={isOpen} size="lg" className="tw-mt-2 tw-pt-0">
      <div className="modal-header">
        <h5 className="modal-title" id="myModalLabel">
          Copy and Paste Portfolio Allocations
        </h5>
      </div>
      <div className="modal-body">
        <p className="mb-1">Paste your tickers and allocations here</p>
        <div
          contentEditable
          onPaste={handlePaste}
          onBeforeInput={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
          onCut={(e) => e.preventDefault()}
          className="tw-grid tw-grid-cols-2 tw-gap-4 tw-text-gray-800 tw-min-h-[150px] tw-p-2 tw-border tw-border-[#ccc] tw-border-solid tw-select-none"
        >
          <div className="">
            <p className="mt-1">
              Please paste tickers in the first column and allocations in the second column (third and fourth for
              portfolio comparison). For Canadian listed equities and exchange-traded funds, please add :TSX or :NEO to
              the ticker according to which exchange the asset is listed on.
            </p>
            <div className="tw-text-center">
              <FontAwesomeIcon icon={faPaste} size="3x" className="" />
            </div>
          </div>
          <div className="">
            <ExampleDataTable />
          </div>
        </div>
        <SpreadsheetData data={data} searched={searched} isMulti={isMulti} />
      </div>
      <div className="modal-footer tw-justify-between tw-px-10">
        <div></div>
        <div>
          <Button className="btn-round" color="info" data-dismiss="modal" type="button" onClick={() => onClose()}>
            Cancel
          </Button>
          {searched && (
            <Button className="btn-round" color="success" data-dismiss="modal" type="button" onClick={handleSubmit}>
              Submit
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default CustomDataModal;
